import { subDays } from "date-fns";
import { NO_COUNTERPARTY_LABEL } from "@common/constants/domains/accounting/schedules";
import { SchedulesReportDrill } from "@common/schemas/domains/accounting/schedules-report.schema";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import {
    ScheduleJeSubtype,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import {
    SchedulesReportLayout,
    SchedulesReportRowKind,
    SchedulesRollforwardColumn,
} from "@common/types/reports/schedules-report";
import { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import { AccountingReportFilters } from "@components/accounting/types";
import { parseDateOnly } from "./schedulesReportDates";
import {
    SCHEDULES_HUB_TABS,
    SchedulesHubTab,
} from "@components/accounting/schedules/SchedulesHubTabs";
import { findScheduleHubScope } from "@components/accounting/schedules/scheduleHubScope";

export interface SchedulesReportDrillDownParams {
    drill: SchedulesReportDrill;
    period: ReportPeriodWithLabel;
    layout: SchedulesReportLayout;
    column: SchedulesRollforwardColumn | null;
}

export interface SchedulesReportOpenScheduleParams {
    tab?: SchedulesHubTab;
    /** Picks the hub; without it the link opens revenue, which redirects a policy of another type. */
    scheduleType?: ScheduleType;
    templateId?: string;
    scheduleApplicationId?: string;
    applicationIds?: string[];
    counterpartyName?: string;
    periodStart?: string;
    periodEnd?: string;
}

export interface SchedulesReportTableCallbacks {
    onDrillDown?: (params: SchedulesReportDrillDownParams) => void;
    onOpenSchedule?: (params: SchedulesReportOpenScheduleParams) => void;
}

export type SchedulesReportTableMeta = ViewTableMeta<
    SchedulesReportTableCallbacks,
    unknown
>;

const NON_RECOGNIZED_SUBTYPES = Object.values(ScheduleJeSubtype).filter(
    (subtype) => subtype !== ScheduleJeSubtype.recognized,
);

interface BuildDrillDownFiltersParams extends SchedulesReportDrillDownParams {
    baseFilters: Pick<AccountingReportFilters, "entityId" | "ledgerId">;
}

/**
 * Maps a clicked cell to GL filters: Beginning shows everything before the
 * column, Ending everything through it, Additions only the re-routed source
 * lines, Recognized only recognition lines, Adjustments the remaining
 * schedule-tagged lines.
 */
export function buildSchedulesReportDrillDownFilters({
    drill,
    period,
    layout,
    column,
    baseFilters,
}: BuildDrillDownFiltersParams): AccountingReportFilters {
    const periodStart = parseDateOnly(period.startDate);
    const periodEnd = parseDateOnly(period.endDate);
    const common: AccountingReportFilters = {
        entityId: baseFilters.entityId,
        ledgerId: baseFilters.ledgerId,
        ...(drill.holdingAccountCode !== undefined && {
            accountCodes: [drill.holdingAccountCode],
        }),
        scheduleApplicationIds: drill.scheduleApplicationIds,
        counterpartiesIds: drill.counterpartyId
            ? [drill.counterpartyId]
            : undefined,
    };
    if (layout === SchedulesReportLayout.waterfall) {
        return {
            ...common,
            startDate: periodStart,
            endDate: periodEnd,
            scheduleJeSubtypes: [ScheduleJeSubtype.recognized],
        };
    }
    switch (column) {
        case SchedulesRollforwardColumn.beginning:
            return {
                ...common,
                endDate: subDays(periodStart, 1),
                isAllTime: true,
            };
        case SchedulesRollforwardColumn.ending:
            return { ...common, endDate: periodEnd, isAllTime: true };
        case SchedulesRollforwardColumn.additions:
            return {
                ...common,
                startDate: periodStart,
                endDate: periodEnd,
                journalEntrySources: [
                    JournalEntrySourceFilter.Transaction,
                    JournalEntrySourceFilter.Invoice,
                ],
            };
        case SchedulesRollforwardColumn.recognized:
            return {
                ...common,
                startDate: periodStart,
                endDate: periodEnd,
                scheduleJeSubtypes: [ScheduleJeSubtype.recognized],
            };
        default:
            return {
                ...common,
                startDate: periodStart,
                endDate: periodEnd,
                scheduleJeSubtypes: NON_RECOGNIZED_SUBTYPES,
            };
    }
}

export function buildScheduleHubUrl({
    tab = SCHEDULES_HUB_TABS.schedules,
    scheduleType = ScheduleType.revenue,
    templateId,
    scheduleApplicationId,
    applicationIds,
    counterpartyName,
    periodStart,
    periodEnd,
}: SchedulesReportOpenScheduleParams): string {
    const params = new URLSearchParams({ tab });
    if (templateId) {
        params.set("templateId", templateId);
    }
    if (scheduleApplicationId) {
        params.set("applicationId", scheduleApplicationId);
    }
    if (applicationIds && applicationIds.length > 0) {
        params.set("applicationIds", applicationIds.join("_"));
    }
    if (counterpartyName) {
        params.set("counterparty", counterpartyName);
    }
    if (periodStart) {
        params.set("periodStart", periodStart);
    }
    if (periodEnd) {
        params.set("periodEnd", periodEnd);
    }
    return `${findScheduleHubScope(scheduleType).path}?${params.toString()}`;
}

export function scheduleReportTitleTarget(row: {
    rowKind: SchedulesReportRowKind;
    counterpartyName?: string | null;
    templateId?: string;
    scheduleApplicationId?: string;
}): SchedulesReportOpenScheduleParams | null {
    if (
        row.rowKind === SchedulesReportRowKind.counterparty ||
        row.rowKind === SchedulesReportRowKind.startPeriod
    ) {
        if (
            !row.counterpartyName ||
            row.counterpartyName === NO_COUNTERPARTY_LABEL
        ) {
            return row.rowKind === SchedulesReportRowKind.startPeriod &&
                row.templateId
                ? { templateId: row.templateId }
                : null;
        }
        return {
            counterpartyName: row.counterpartyName,
            ...(row.templateId && { templateId: row.templateId }),
        };
    }
    if (
        row.rowKind === SchedulesReportRowKind.application &&
        row.scheduleApplicationId
    ) {
        return { scheduleApplicationId: row.scheduleApplicationId };
    }
    if (row.rowKind === SchedulesReportRowKind.template && row.templateId) {
        return { templateId: row.templateId };
    }
    return null;
}
